import { AuthLayoutComponent } from '@stockcare-components/layouts/auth-layout/auth-layout.component';
import { MainLayoutComponent } from '@stockcare-components/layouts/main-layout/main-layout.component';
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuardService } from '@stockcare-guards/auth-guard.service';
import { AuthorisationAuthGuardService } from '@stockcare-guards/authorisation-auth-guard.service';
import { LogoutComponent } from '@stockcare-views/authentication/logout/logout.component';
import { Roles } from '@stockcare-helpers/roles';
import { ActionCenterAuthGuardService } from '@stockcare-guards/action-center-auth-gaurd.service';
import { PinComponent } from '@stockcare-views/authentication/pin/pin.component';

const appRoutes: Routes = [
    {
        path: '',
        redirectTo: 'app',
        pathMatch: 'full',
    },
    {
        path: 'app',
        children: [
            {
                path: '',
                redirectTo: 'authentication/login',
                pathMatch: 'full',
            },
            {
                path: 'authentication',
                redirectTo: 'authentication/login',
                pathMatch: 'full',
            },
            {
                path: 'signout',
                component: LogoutComponent,
            },
            {
                path: '',
                component: AuthLayoutComponent,
                children: [
                    {
                        path: 'authentication',
                        loadChildren: () => import('./views/authentication/authentication.module').then((m) => m.AuthenticationModule),
                    },
                ],
            },
            {
                path: '',
                component: MainLayoutComponent,
                canActivate: [AuthGuardService],
                children: [
                    {
                        path: 'patients',
                        loadChildren: () => import('./views/patients/patient.module').then((m) => m.PatientModule),
                        data: {
                            roles: [Roles.systemAdministrator, Roles.superAdministrator, Roles.stockIssuer, Roles.creditRequester],
                        },
                    },
                    {
                        path: 'devices',
                        loadChildren: () => import('./views/devices/device.module').then((m) => m.DeviceModule),
                        canActivate: [AuthorisationAuthGuardService],
                        data: {
                            roles: [Roles.systemAdministrator, Roles.superAdministrator],
                        },
                    },
                    {
                        path: 'materials',
                        loadChildren: () => import('./views/materials/materials.module').then((m) => m.MaterialsModule),
                    },
                    {
                        path: 'users',
                        loadChildren: () => import('./views/users/users.module').then((m) => m.UsersModule),
                        canActivate: [AuthorisationAuthGuardService],
                        data: {
                            roles: [Roles.systemAdministrator, Roles.superAdministrator, Roles.hospitalAdministrator],
                        },
                    },
                    {
                        path: 'settings',
                        loadChildren: () => import('./views/settings/settings.module').then((m) => m.SettingsModule),
                        canActivate: [AuthorisationAuthGuardService],
                        data: {
                            roles: [Roles.systemAdministrator, Roles.superAdministrator],
                        },
                    },
                    {
                        path: 'contact-management',
                        loadChildren: () => import('./views/contact-management/contact.module').then((m) => m.ContactModule),
                        canActivate: [AuthorisationAuthGuardService],
                        data: {
                            roles: [Roles.systemAdministrator, Roles.superAdministrator],
                        },
                    },

                    {
                        path: 'approvals',
                        loadChildren: () => import('./views/credit/credit.module').then((m) => m.CreditModule),
                        canActivate: [AuthorisationAuthGuardService],
                        data: {
                            roles: [Roles.systemAdministrator, Roles.superAdministrator, Roles.creditApprover],
                        },
                    },
                    {
                        path: 'baskets',
                        loadChildren: () => import('./views/baskets/baskets.module').then((m) => m.BasketsModule),
                        canActivate: [AuthorisationAuthGuardService],
                        data: {
                            roles: [Roles.systemAdministrator, Roles.superAdministrator, Roles.hospitalAdministrator, Roles.creditApprover],
                        },
                    },
                    {
                        path: 'theatre',
                        loadChildren: () => import('./views/theatre-care/theatre-care.module').then((m) => m.TheatreCareModule),
                        canActivate: [AuthorisationAuthGuardService],
                        data: {
                            roles: [Roles.superAdministrator, Roles.systemAdministrator, Roles.theatre],
                        },
                    },
                    {
                        path: 'reporting',
                        loadChildren: () => import('./views/reporting/reporting.module').then((m) => m.ReportingModule),
                        canActivate: [AuthorisationAuthGuardService],
                        data: {
                            roles: [Roles.superAdministrator, Roles.systemAdministrator, Roles.reportViewer],
                        },
                    },
                    {
                        path: 'emergency-cupboard',
                        loadChildren: () => import('./views/ward-list/ward-list.module').then((m) => m.EmergencyCupboardModule),
                        canActivate: [AuthorisationAuthGuardService],
                        data: {
                            roles: [Roles.emergencyCupboardStockIssuer, Roles.systemAdministrator, Roles.superAdministrator],
                        },
                    },
                    {
                        path: 'whiteboard',
                        loadChildren: () => import('./views/whiteboard/whiteboard.module').then((m) => m.WhiteboardModule),
                        data: {
                            roles: [Roles.superAdministrator, Roles.systemAdministrator],
                        },
                    },
                    // {
                    //     path: 'action-center',
                    //     loadChildren: () => import('./views/action-center/action-center.module').then((m) => m.ActionCenterModule),
                    //     canActivate: [ActionCenterAuthGuardService],
                    //     data: {
                    //         roles: [Roles.superAdministrator, Roles.systemAdministrator],
                    //     },
                    // },
                    {
                        path: 'material-transfer',
                        loadChildren: () => import('./views/material-transfer/material-transfer.module').then((m) => m.MaterialTransferModule),
                        canActivate: [AuthorisationAuthGuardService],
                        data: {
                            roles: [Roles.superAdministrator, Roles.systemAdministrator, Roles.materialTransfer],
                        },
                    },
                    {
                        path: 'theatre-default-config',
                        loadChildren: () => import('./views/theatre-visit-default-configuration/theatre-visit-default-configuration.module').then((m) => m.TheatreManagementModule),
                        canActivate: [AuthorisationAuthGuardService],
                        data: {
                            roles: [Roles.superAdministrator, Roles.systemAdministrator],
                        },
                    },
                    {
                        path: 'pack-management',
                        loadChildren: () => import('./views/pack-management/pack-management.module').then((m) => m.PackManagementModule),
                        canActivate: [AuthorisationAuthGuardService],
                        data: {
                            roles: [Roles.superAdministrator, Roles.systemAdministrator],
                        },
                    },
                    {
                        path: 'request-training',
                        loadChildren: () => import('./views/request-training/request-training.module').then((m) => m.RequestTrainingModule),
                        canActivate: [AuthorisationAuthGuardService],
                        data: {
                            roles: [Roles.systemAdministrator, Roles.superAdministrator, Roles.hospitalAdministrator, Roles.stockIssuer, Roles.creditRequester, Roles.theatre],
                        },
                    },
                    {
                        path: 'my-voice-surveys',
                        loadChildren: () => import('./views/my-voice-surveys/my-voice-surveys.module').then((m) => m.MyVoiceSurveysModule),
                        canActivate: [AuthorisationAuthGuardService],
                        data: {
                            roles: [Roles.systemAdministrator, Roles.superAdministrator, Roles.hospitalAdministrator, Roles.creditApprover],

                        },
                    },
                    {
                        path: 'pre-admission',
                        loadChildren: () => import('./views/pre-admission/pre-admission-module').then((m) => m.PreAdmissionModule),
                        canActivate: [AuthorisationAuthGuardService],
                        data: {
                            roles: [Roles.preAdmission],
                        },
                    },
                    {
                        path: 'tis-scoring',
                        loadChildren: () => import('./views/tis-scoring/tis-scoring-module').then((m) => m.TisScoringModule),
                        canActivate: [AuthorisationAuthGuardService],
                        data: {
                            roles: [Roles.tisScoring, Roles.superAdministrator, Roles.hospitalAdministrator],
                        },
                    },
                    {
                        path: 'tis-scoring-calculation',
                        loadChildren: () => import('./views/tis-scoring-calculation/tis-scoring-calculation-module').then((m) => m.TisScoringCalculationModule),
                        canActivate: [AuthorisationAuthGuardService],
                        data: {
                            roles: [Roles.superAdministrator],
                        },
                    },
                    {
                        path: 'stock-counting',
                        loadChildren: () => import('./views/stock-counting/stock-counting-module').then((m) => m.StockCountingModule),
                        canActivate: [AuthorisationAuthGuardService],
                        data: {
                            roles: [Roles.stockCountManager, Roles.superAdministrator, Roles.hospitalAdministrator],
                        },
                    },
                    {
                        path: 'survey-escalation-management',
                        loadChildren: () => import('./views/survey-escalation-management/survey-escalation-management-module').then((m) => m.SurveyEscalationManagementModule),
                        canActivate: [AuthorisationAuthGuardService],
                        data: {
                            roles: [Roles.systemAdministrator, Roles.superAdministrator, Roles.hospitalAdministrator],
                        },
                    },
                ],
            },
        ],
    },
    {
        path: '**',
        redirectTo: 'app/authentication/login',
    },
];

@NgModule({
    imports: [RouterModule.forRoot(appRoutes, {})],
    exports: [RouterModule],
})
export class AppRoutingModule { }
